import { Link } from 'react-router-dom';

const FEATURES = [
  {
    title: 'On-Premise AI',
    description:
      'All data processing happens locally on your infrastructure. Your data never leaves your premises -- secure on-premise AI with full operational control.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-2.47 2.47a2.25 2.25 0 00-.659 1.59v1.69a2.25 2.25 0 01-2.25 2.25h-3.242a2.25 2.25 0 01-2.25-2.25v-1.69a2.25 2.25 0 00-.659-1.59L5 14.5m14 0V8.25a2.25 2.25 0 00-2.25-2.25h-9.5A2.25 2.25 0 005 8.25v6.25" />
      </svg>
    ),
  },
  {
    title: 'Operational Security',
    description:
      'Designed for organizations that require strict data control. Your data stays on your hardware, fully compliant with local regulations.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
    ),
  },
  {
    title: 'Ruggedized Hardware',
    description:
      'Purpose-built for demanding environments. Our appliances are engineered for reliability in the field with industrial-grade components.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
  },
  {
    title: 'Dedicated Support',
    description:
      'Direct access to our engineering team. We provide hands-on integration support and long-term partnership for every deployment.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
];

export default function Home() {
  return (
    <div className="relative overflow-hidden">
      {/* ---- Background gradient effects ---- */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/5 blur-3xl" />
      </div>

      {/* ==================== HERO ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-36 lg:pb-32">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-sm text-cyan-400 font-medium">Enterprise AI Appliances — On-Premise, On-Demand</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
            Secure AI{' '}
            <span className="text-gradient">Appliances</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            AIBOX appliances bring GPU-accelerated AI to on-premise deployments &mdash;
            delivering real-time analytics, secure data processing, and intelligent automation
            where operational security matters most.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/aibox"
              className="px-8 py-3 btn-gradient text-white font-semibold rounded-lg text-base"
            >
              Explore AI-Box
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 border border-navy-600 hover:border-cyan-500/50 text-slate-300 hover:text-white font-semibold rounded-lg text-base transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Why Choose <span className="text-gradient">TRDATA</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            Secure, reliable AI solutions designed for organizations that demand operational
            security and on-premise control.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300"
            >
              {/* Glow on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 mb-6">
                  {f.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{f.title}</h3>
                <p className="text-slate-400 leading-relaxed">{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pb-28">
        <div className="relative bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-10 sm:p-16 text-center overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-cyan-500/10 blur-2xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-blue-500/10 blur-2xl" />

          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Ready to Secure Your Operations?
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Get in touch with our solutions team to discuss how AIBOX can enhance
              your operational capabilities.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/aibox"
                className="px-8 py-3 btn-gradient text-white font-semibold rounded-lg"
              >
                View AI-Box
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3 border border-navy-600 hover:border-cyan-500/50 text-slate-300 hover:text-white font-semibold rounded-lg transition-colors"
              >
                Request a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
